import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { PenLine, Feather } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { LetterCard } from '@/components/LetterCard';
import { LetterCardSkeleton } from '@/components/LetterCardSkeleton';
import { Button } from '@/components/ui/button';
import { useWisdomLetters } from '@/hooks/useWisdomLetters';

const TOPICS = ['All', 'relationships', 'career', 'health', 'money', 'family', 'mindset', 'education', 'travel'];

export default function Index() {
  useSeoMeta({
    title: 'Hindsight Wisdom — Letters to Our Past Selves',
    description: 'A community of people sharing what they wish they had known. Read and write letters to your past self on Nostr.',
  });

  const [activeTopic, setActiveTopic] = useState<string | undefined>(undefined);
  const { data: letters, isLoading, isError } = useWisdomLetters({ topic: activeTopic });

  return (
    <Layout>
      {/* Hero */}
      <section className="relative isolate overflow-hidden border-b border-border">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-primary/8 via-background to-accent/20" />
        <div className="container py-16 sm:py-20 text-center">
          <div className="mx-auto max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-sm text-muted-foreground mb-6 shadow-sm">
              <Feather className="w-3.5 h-3.5" />
              <span>Letters stored forever on Nostr</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-foreground leading-tight mb-5">
              What would you tell
              <br />
              <span className="text-primary italic">your younger self?</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-lg mx-auto">
              A community of honest letters. The lessons we wish we'd learned sooner — shared openly so others can learn faster.
            </p>
            <Link to="/write">
              <Button size="lg" className="rounded-full gap-2 px-8 shadow-sm">
                <PenLine className="w-4 h-4" />
                Write your letter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Feed */}
      <section className="container py-10">
        {/* Topic filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {TOPICS.map((topic) => {
            const value = topic === 'All' ? undefined : topic;
            const isActive = activeTopic === value;
            return (
              <button
                key={topic}
                onClick={() => setActiveTopic(value)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${
                  isActive
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                }`}
              >
                {topic}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        {isLoading && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <LetterCardSkeleton key={i} />
            ))}
          </div>
        )}

        {isError && (
          <div className="border border-dashed border-border rounded-xl py-16 px-8 text-center">
            <p className="text-muted-foreground">
              Could not load letters. Check your relay connection and try again.
            </p>
          </div>
        )}

        {!isLoading && !isError && letters && letters.length === 0 && (
          <div className="border border-dashed border-border rounded-xl py-16 px-8 text-center">
            <Feather className="w-10 h-10 text-muted-foreground/40 mx-auto mb-4" />
            <p className="font-serif text-lg text-foreground mb-2">No letters yet{activeTopic ? ` in "${activeTopic}"` : ''}.</p>
            <p className="text-muted-foreground text-sm mb-6">Be the first to share your hindsight wisdom.</p>
            <Link to="/write">
              <Button variant="outline" className="rounded-full gap-2">
                <PenLine className="w-4 h-4" />
                Write the first letter
              </Button>
            </Link>
          </div>
        )}

        {!isLoading && !isError && letters && letters.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 motion-safe:animate-[fade-in_0.4s_ease-out]">
            {letters.map((letter) => (
              <LetterCard key={letter.event.id} letter={letter} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
